import React, { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { EmptyState, InlineLoader } from "@/components/shared";
import { Heart, MessageCircle, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";

const timeAgo = (iso) => {
  try {
    const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return "just now";
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch { return ""; }
};

export default function WordsOfSupport({ campaignId, defaultName = "" }) {
  const [items, setItems] = useState(null);
  const [name, setName] = useState(defaultName);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [hearted, setHearted] = useState({});

  useEffect(() => {
    api.get(`/campaigns/${campaignId}/comments`).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [campaignId]);

  const submit = async () => {
    if (name.trim().length < 1) return toast.error("Please add your name.");
    if (body.trim().length < 2) return toast.error("Please write a short message.");
    setBusy(true);
    try {
      const { data } = await api.post(`/campaigns/${campaignId}/comments`, { author_name: name.trim(), body: body.trim() });
      setItems((prev) => [data, ...(prev || [])]);
      setBody("");
      toast.success("Thanks for your support! 💙");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const heart = async (id) => {
    if (hearted[id]) return;
    setHearted((h) => ({ ...h, [id]: true }));
    setItems((prev) => prev.map((c) => (c.id === id ? { ...c, hearts: (c.hearts || 0) + 1 } : c)));
    try { await api.post(`/campaigns/${campaignId}/comments/${id}/heart`); } catch { /* keep optimistic */ }
  };

  return (
    <div data-testid="words-of-support-wall">
      <div className="rounded-2xl border border-ff-border bg-[#F9FAFB] p-5">
        <div className="flex items-center gap-2 font-heading text-sm font-bold text-ff-blue">
          <MessageCircle className="h-4 w-4 text-ff-action" /> Leave a word of support
        </div>
        <p className="mt-1 text-xs text-ff-medium">You don't have to donate to cheer them on — encouragement matters.</p>
        <div className="mt-3 grid gap-2 sm:grid-cols-[200px_1fr]">
          <Input data-testid="wos-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" maxLength={80} />
          <Textarea data-testid="wos-body" value={body} onChange={(e) => setBody(e.target.value)} rows={2} placeholder="Say something kind…" maxLength={1000} />
        </div>
        <Button onClick={submit} disabled={busy} data-testid="wos-submit" className="mt-3 bg-ff-action font-semibold text-white hover:bg-ff-actionDark">
          {busy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Posting…</> : <><Send className="mr-2 h-4 w-4" /> Post message</>}
        </Button>
      </div>

      <div className="mt-5">
        {items === null ? <InlineLoader label="Loading messages" /> : items.length === 0 ? (
          <EmptyState icon={MessageCircle} title="No messages yet" message="Be the first to leave a word of support." testid="wos-empty" />
        ) : (
          <div className="space-y-3" data-testid="wos-list">
            {items.map((c) => (
              <div key={c.id} className="rounded-xl border border-ff-border bg-white p-4" data-testid={`wos-item-${c.id}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2.5">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-ff-blue text-sm font-bold text-white">{c.author_name?.[0]?.toUpperCase() || "?"}</span>
                    <div>
                      <div className="text-sm font-semibold text-ff-blue">{c.author_name}</div>
                      <div className="text-xs text-ff-medium">{timeAgo(c.created_at)}</div>
                    </div>
                  </div>
                  <button onClick={() => heart(c.id)} data-testid={`wos-heart-${c.id}`}
                    className={`flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-bold transition-colors duration-200 ${hearted[c.id] ? "border-ff-red bg-[#FEF2F2] text-ff-red" : "border-ff-border text-ff-medium hover:border-ff-red hover:text-ff-red"}`}>
                    <Heart className={`h-3.5 w-3.5 ${hearted[c.id] ? "fill-current" : ""}`} /> {c.hearts || 0}
                  </button>
                </div>
                <p className="mt-2.5 whitespace-pre-line text-sm leading-relaxed text-ff-dark">{c.body}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
